<template>
  <div>
    <div class="affiliate-kpi-bonus-popup">
      <div class="popup">
        <div class="head">
          <span class="name">{{ `${$t(`affiliates.affiliateKpiBonuses.popup.${action}_title`)}` }}</span>
          <UiIcon
            :size="19"
            class="close"
            name="times"
            @click="close"
          />
        </div>
        <div
          v-loading="isDataLoading"
          class="form"
        >
          <UiSelectElement
            v-model="kpiType"
            class="t:mb-16"
            :placeholder="$t('affiliates.affiliateKpiBonuses.popup.selectKpiType')"
            :label="$t('affiliates.affiliateKpiBonuses.popup.kpiType')"
            :list="kpiTypeOptions"
            option-name-param="text"
            option-label-param="text"
            option-value-param="key"
            size="big"
            filterable
            :error="getError('type')"
          />
          <UiInput
            v-model="goal"
            class="t:mb-16"
            :label="$t('affiliates.affiliateKpiBonuses.popup.setGoal')"
            type="number"
            autosize
            size="big"
            :error="errors?.goal"
          />
          <UiRadioGroup
            v-model="rateType"
            :label="$t('affiliates.affiliateKpiBonuses.popup.setRate')"
            class="t:mb-16"
            :options="[
              {
                label: $t('affiliates.affiliateKpiBonuses.popup.percent'),
                value: 'percent',
              },
              {
                label: $t('affiliates.affiliateKpiBonuses.popup.fixed'),
                value: 'fixed',
              }
            ]"
          >
            <UiInput
              v-if="rateType === 'percent'"
              v-model="bonusPayoutRate"
              type="number"
              autosize
              post-text="%"
              size="big"
              :error="errors?.bonus_payout_rate"
            />
            <div
              v-else
              class="t:flex"
            >
              <UiInput
                v-model="bonusPayoutSum"
                class="t:mr-8"
                type="number"
                autosize
                size="big"
                :error="errors?.bonus_payout_sum"
              />
              <UiSelectElement
                v-model="bonusPayoutCurrencyCode"
                :placeholder="$t('settings.currencies.currency')"
                :list="customerCurrencies"
                option-value-param="code"
                size="big"
                filterable
                :error="getError('bonus_payout_currency_code')"
              />
            </div>
          </UiRadioGroup>
          <UiSelectElement
            v-model="periodValue"
            :label="$t('affiliates.affiliateKpiBonuses.popup.selectPeriod')"
            :placeholder="$t('permissions.offers.not_selected')"
            :list="periodOptions"
            option-value-param="key"
            size="big"
            filterable
          />
          <div class="t:flex t:mt-8 t:items-start  t:w-full">
            <UiDatePicker
              v-model="period[0]"
              class="t:w-full"
              :disabled="periodValue !== 'custom'"
              :placeholder="$t('reports.other.start_date')"
              :multi-calendars="false"
              :range="false"
              auto-apply
              :always-clearable="false"
              size="big"
              :error="getError('period_start')"
            />
            <span class="t:ml-8 t:mr-8 t:mt-8">&ndash;</span>
            <UiDatePicker
              v-model="period[1]"
              class="t:w-full"
              :disabled="periodValue !== 'custom'"
              :placeholder="$t('reports.other.end_date')"
              :multi-calendars="false"
              :range="false"
              auto-apply
              :always-clearable="false"
              size="big"
              :error="getError('period_end')"
            />
          </div>
        </div>
        <div class="foot t:space-x-8">
          <UiButton
            size="big"
            type="negative"
            icon="times"
            @click="close"
          >
            {{ $t('crm.buttons.close') }}
          </UiButton>
          <UiButton
            filled
            size="big"
            icon="check"
            @click="action === 'add' ? addKpiBonuses() : updateBonus()"
          >
            {{ action === 'add' ? $t('affiliates.affiliateKpiBonuses.popup.add_button') : $t('affiliates.affiliateKpiBonuses.popup.edit_button') }}
          </UiButton>
        </div>
      </div>
      <div class="wrapper" />
    </div>
  </div>
</template>

<script>


import { mapGetters } from 'vuex';
import { addDays, format } from 'date-fns';
import errorHandleMixin from '@/views/mixins/error-hadle';

export default {
  name: 'AffiliateKpiBonusPopup',
  mixins: [errorHandleMixin],
  props: {
    userId: {
      type: String,
      default() {
        return '';
      },
    },
    data: {
      type: Object,
      default() {
        return {};
      },
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['close'],
  data() {
    return {
      isDataLoading: false,
      id: null,
      goal: 0,
      kpiType: '',
      bonusPayoutRate: 0,
      bonusPayoutCurrencyCode: '',
      bonusPayoutSum: 0,
      periodValue: 'custom',
      rateType: 'percent',
      period: [],
      action: 'add',
      errors: {},
    };
  },
  computed: {
    ...mapGetters({
      customerCurrencies: 'misc/customerCurrencies',
    }),
    periodOptions() {
      return [
        {
          key: 'custom',
          name: this.$t('ui.shortcuts.custom_range'),
        },
        {
          key: '7',
          name: this.$t('ui.shortcuts.days_7'),
        },
        {
          key: '14',
          name: this.$t('ui.shortcuts.days_14'),
        },
        {
          key: '30',
          name: this.$t('ui.shortcuts.days_30'),
        },
      ];
    },
    kpiTypeOptions() {
      return this.$store.state.affiliate.kpiBonusTypes.map((el) => ({
        key: el.type,
        text: el.title,
      }));
    },
    paramsAttach() {
      const params = {
        affiliate_id: this.userId || undefined,
        type: this.kpiType,
        goal: this.goal,
        ...this.rateParams,
      };

      if(!this.period.length){
        params.period_start = format(new Date, 'yyyy-MM-dd');
        params.period_end = format(new Date, 'yyyy-MM-dd');
      } else {
        params.period_start = format(this.period[0], 'yyyy-MM-dd');
        params.period_end = format(this.period[1], 'yyyy-MM-dd');
      }

      return params;
    },
    rateParams() {
      if (this.rateType === 'percent') {
        return {
          bonus_payout_currency_code: null,
          bonus_payout_sum: null,
          bonus_payout_rate: +this.bonusPayoutRate,
        };
      }

      return {
        bonus_payout_rate: null,
        bonus_payout_currency_code: this.bonusPayoutCurrencyCode,
        bonus_payout_sum: +this.bonusPayoutSum,
      };
    },
  },
  watch: {
    periodValue(val) {
      if (val === 'custom') {
        return;
      }

      const startDate = addDays(new Date(), 1);
      const endDate = addDays(new Date(), +val);

      this.period = [startDate, endDate];
    },
    isOpen(e) {
      if (e) {
        this.resetForm();
        if (!this.$_.isEmpty(this.data)) {
          const {
            period_start, period_end, goal, type, bonus_payout_rate, bonus_payout_sum, bonus_payout_currency_code, id,
          } = this.data;

          this.kpiType = type;
          this.goal = goal;
          this.period = [period_start, period_end];
          this.id = id;

          if (bonus_payout_rate) {
            this.bonusPayoutRate = bonus_payout_rate;
            this.rateType = 'percent';
          } else {
            this.bonusPayoutSum = bonus_payout_sum;
            this.bonusPayoutCurrencyCode = bonus_payout_currency_code;
            this.rateType = 'fixed';
          }

          this.action = 'edit';
        } else {
          this.action = 'add';
        }
      }

      this.isDataLoading = false;
    },
  },
  methods: {
    addKpiBonuses() {
      this.errors = {};
      this.$api.postAffiliateKpiBonuses(this.paramsAttach)
        .then(() => {
          this.close('reload');
        })
        .catch((error) => {
          this.errors = this.$_.cloneDeepWith(error.data.errors);
        });
    },
    updateBonus() {
      this.errors = {};
      this.$api.putAffiliateKpiBonuses(this.data.id, this.paramsAttach)
        .then(() => {
          this.close('reload');
        })
        .catch((error) => {
          this.errors = this.$_.cloneDeepWith(error.data.errors);
        });
    },
    resetForm() {
      this.isDataLoading = true;

      this.id = null;
      this.goal = 0;
      this.kpiType = '';
      this.bonusPayoutRate = 0;
      this.bonusPayoutCurrencyCode = '';
      this.bonusPayoutSum = 0;
      this.periodValue = 'custom';
      this.rateType = 'percent';
      this.period = [];

      this.errors = {};
    },
    close(v, data) {
      this.$emit('close', v, data);
    },
  },
};
</script>

<style lang="scss" scoped>
.affiliate-kpi-bonus-popup {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 900;
  .popup {
    position: absolute;
    z-index: 902;
    left: calc(50% - 240px);
    top: 10%;
    width: 480px;
    padding-top: 16px;
    padding-bottom: 16px;
    border-radius: 8px;
    background-color: white;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    .head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid #eaeaea;
      padding: 0 24px 16px;
      margin-bottom: 16px;
      .name {
        font-size: 20px;
        font-weight: 500;
      }
      .close {
        color: #303634;
        cursor: pointer;
        padding: 5px;
      }
    }
    .form {
      padding-left: 24px;
      padding-right: 24px;
    }
    .foot {
      display: flex;
      justify-content: flex-end;
      border-top: 1px solid #eaeaea;
      padding: 16px 24px 0;
      margin-top: 16px;
    }
  }
  .wrapper {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    opacity: 0.5;
    background-color: #303634;
    z-index: 901;
  }

  .ui-radio-group {
    // /deep/ {
      :deep(.el-radio) {
        margin-right: 0;
      }

      :deep(.el-radio__label) {
        font-weight: normal;
      }
    // }
  }
}
</style>
